<template>
  <v-text-field
    ref="campo"
    v-model="bozza"
    dense
    outlined
    hide-details
    placeholder="—"
    :readonly="chiusa"
    :disabled="disabilitata"
    :prefix="prefisso"
    :error="errore"
    class="cella_qta"
    :class="{
      piena: valore > 0,
      enfasi: enfasi,
      stato: errore,
      completo: successo,
      con_prefisso: !!prefisso,
    }"
    @focus="al_focus"
    @blur="al_blur"
  >
    <template v-if="bloccabile" v-slot:append-outer>
      <v-icon size="18" color="grey lighten-1" @click="sblocca" class="mb-2">
        {{ chiusa ? "mdi-lock" : "mdi-lock-open-variant" }}
      </v-icon>
    </template>
  </v-text-field>
</template>

<script>
/**
 * Cella editabile di quantità.
 *
 * Non ha debounce né stato di caricamento: da quando le scritture sono in
 * batch, è lo store ad accumulare e spedire. La cella si limita a proporre
 * il valore e a segnalare ogni variazione.
 *
 * La cornice del campo resta invisibile finché non ci si passa sopra o non
 * si entra in modifica: su una griglia di trenta colori quasi tutte le celle
 * sono vuote, e trenta scatole con dentro uno zero nascondono i pochi numeri
 * che contano davvero.
 */
export default {
  props: {
    valore: { type: Number, default: 0 },
    // Prefisso informativo (usato dalla colonna totale per la rimanenza).
    prefisso: { type: String, default: undefined },
    errore: { type: Boolean, default: false },
    disabilitata: { type: Boolean, default: false },
    // Tutto distribuito. Trattamento sobrio di proposito: è lo stato in cui
    // finiscono quasi tutte le righe, e segnalarlo con un riquadro
    // riempirebbe la griglia annullando il guadagno delle celle piatte.
    // Il riquadro resta all'errore, che è raro e richiede un intervento.
    successo: { type: Boolean, default: false },
    // Richiede uno sblocco esplicito: protegge le celle il cui valore è
    // autorevole (il totale prenotato) da modifiche accidentali.
    bloccabile: { type: Boolean, default: false },
    // Colonna portante: si legge prima delle altre.
    enfasi: { type: Boolean, default: false },
    massimo: { type: Number, default: 999 },
  },

  data() {
    return {
      bozza: this.testo(this.valore),
      in_focus: false,
      chiusa: this.bloccabile,
    };
  },

  watch: {
    // Mentre l'utente scrive la bozza è la verità; fuori focus segue lo store,
    // così le modifiche altrui (socket) e le risposte del server si vedono.
    valore(nuovo) {
      if (!this.in_focus) this.bozza = this.testo(nuovo);
    },

    bozza(testo) {
      if (!this.in_focus) return;
      // Campo svuotato per riscrivere: non è ancora un valore, si aspetta.
      if (testo === "") return;

      const n = Number(testo);
      if (isNaN(n)) {
        this.bozza = this.testo(this.valore);
        return;
      }
      if (n < 0) {
        this.bozza = "";
        return;
      }
      if (n > this.massimo) {
        this.bozza = String(this.massimo);
        return;
      }
      this.$emit("cambia", n);
    },
  },

  methods: {
    // Lo zero non si scrive: resta il segnaposto.
    testo(valore) {
      return valore > 0 ? String(valore) : "";
    },

    al_focus(e) {
      if (this.chiusa) return;
      this.in_focus = true;
      e.target.select();
    },

    al_blur() {
      this.in_focus = false;
      if (this.bozza === "") {
        // Uscire da un campo svuotato vale azzeramento.
        if (this.valore) this.$emit("cambia", 0);
      } else {
        this.bozza = this.testo(this.valore);
      }
      if (this.bloccabile) {
        setTimeout(() => {
          this.chiusa = true;
        }, 300);
      }
    },

    sblocca() {
      if (!this.chiusa) return;
      this.chiusa = false;
      this.$nextTick(() => this.$refs.campo.focus());
    },
  },
};
</script>

<style scoped>
.cella_qta >>> input {
  text-align: right;
  /* Cifre a larghezza fissa: una colonna di numeri si scorre solo se sono
     incolonnati. */
  font-variant-numeric: tabular-nums;
}

/* Geometria fissata, non lasciata ai default di Vuetify: GrigliaColori usa
   questi numeri per allineare i totali delle righe prodotto e gruppo ai
   numeri delle celle. Cambiarli qui richiede di aggiornarli anche là. */
.cella_qta >>> .v-input__slot {
  padding-left: 8px;
  padding-right: 8px;
}

.cella_qta >>> .v-input__append-outer {
  margin-left: 8px;
}

/* Cornice nascosta a riposo, mostrata dove si sta per intervenire.
   Solo l'errore la tiene sempre visibile. */
.cella_qta:not(.stato) >>> fieldset {
  border-color: transparent;
  transition: border-color 0.15s;
}

.cella_qta:not(.stato):hover >>> fieldset,
.cella_qta:not(.stato).v-input--is-focused >>> fieldset {
  border-color: rgba(0, 0, 0, 0.28);
}

/* Il valore c'è: si legge come numero, non come contenuto di un campo. */
.cella_qta.piena >>> input {
  font-size: 1.02rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}

/* Colonna del prenotato: è il numero autorevole, le consegne lo ripartiscono. */
.cella_qta.enfasi.piena >>> input {
  font-size: 1.12rem;
  font-weight: 600;
}

/* Col prefisso l'input riempirebbe la colonna e il numero, allineato a
   destra, finirebbe lontanissimo dal "0 /". Qui i due si accostano: lo slot
   spinge tutto a destra e l'input si restringe al proprio contenuto. */
.cella_qta.con_prefisso >>> .v-text-field__slot {
  justify-content: flex-end;
}

.cella_qta.con_prefisso >>> input {
  flex: 0 0 auto;
  width: 4ch;
}

/* Tutto distribuito: verde sul numero, nessun riquadro. */
.cella_qta.completo >>> input {
  color: #2e7d32;
}

.cella_qta.completo >>> .v-text-field__prefix {
  color: #66bb6a;
}

.cella_qta >>> input::placeholder {
  color: rgba(0, 0, 0, 0.2);
  opacity: 1;
}

.cella_qta >>> .v-text-field__prefix {
  color: rgba(0, 0, 0, 0.38);
  font-size: 0.8rem;
}

/* Vuetify riserva all'append-outer un margine pensato per i messaggi di
   validazione, che qui non ci sono (hide-details): senza correzione il
   lucchetto resta sopra la linea del numero. L'allineamento finale è dato da
   questo blocco insieme alla classe mb-3 sull'icona nel template. */
.cella_qta >>> .v-input__append-outer {
  margin-top: 0;
  margin-bottom: 0;
  align-self: center;
}
</style>
